import { useEffect, useRef, useState } from 'react'
import ReactWebChat from 'botframework-webchat'
import { useDispatch, useSelector } from 'react-redux'
import { FullReactWebChatProps } from 'botframework-webchat/lib/FullReactWebChat'
import type { AppDispatch, RootState } from '@/store/redux-store'
import { registerActivityCallback } from '@/store/modules/webchat'
import { closed, overlaySubmitted } from '@/store/modules/bot-view'
import { Overlay } from '../overlay'
import { useStore } from '../../hooks/use-store'
import { useWebchatRuntime } from '../../hooks/use-webchat-runtime'
import { editUserActivityMiddleware } from '../../decorators/edit-user-activity/middleware'
import { ModelType } from '../../types/model'
import type { ActivityCallback, EventPayload, MessagePayload } from '../../types/common'
import { handleWebChatActivity } from './activity-callback'
import { WebChatContainer } from './styles'

// There is a type mismatch between the botframework version of react JSX types
// and Vue JSX when compiled via vue-cli. We can remove this once we migrate to React fully.
type ReactWebChatType = (props: FullReactWebChatProps) => JSX.Element
const ReactWebChatComponent = ReactWebChat as ReactWebChatType

type Props = {
  styleOptions: Record<string, unknown>
  showOverlay: boolean
  model?: ModelType
}

/** Private Direct Line host. WebChat is the only public export from this folder. */
const Chat = ({ styleOptions, showOverlay = false, model }: Props) => {
  const { botConnection, store: webChatStore } = useWebchatRuntime()
  const { sendEvent, sendMessage } = useStore()
  const dispatch = useDispatch<AppDispatch>()
  const container = useSelector((state: RootState) => String(state.storage.container ?? ''))
  const sas = useSelector((state: RootState) => String(state.storage.sas ?? ''))
  const uri = useSelector((state: RootState) => String(state.storage.uri ?? ''))
  const [hasSuggestedActions, setHasSuggestedActions] = useState(false)
  const [hasAdaptiveCard, setHasAdaptiveCard] = useState(false)
  const sendMessageRef = useRef(sendMessage)
  sendMessageRef.current = sendMessage

  const onSendEvent = (payload: EventPayload) => {
    sendEvent(payload)
  }

  const onSendMessage = (payload: MessagePayload) => {
    sendMessage(payload)
  }

  useEffect(() => {
    if (!botConnection) return

    const subscription = dispatch(
      registerActivityCallback(activity => {
        const next = handleWebChatActivity(
          activity as Parameters<typeof handleWebChatActivity>[0],
          {
            sendMessage: payload => sendMessageRef.current(payload),
          },
        )
        setHasSuggestedActions(next.hasSuggestedActions)
        setHasAdaptiveCard(next.hasAdaptiveCard)
      }),
    )
    return () => subscription?.unsubscribe?.()
  }, [botConnection, dispatch])

  const handleClose = () => {
    dispatch(closed())
  }

  const handleOverlaySubmit = () => {
    dispatch(overlaySubmitted())
  }

  const onRegisterActivityCallback = (callback: ActivityCallback) => {
    return dispatch(registerActivityCallback(callback as (activity: unknown) => void))
  }

  return (
    <WebChatContainer
      id="webchat"
      $hasSuggestedActions={hasSuggestedActions}
      $hasAdaptiveCard={hasAdaptiveCard}
    >
      {showOverlay && model && (
        <Overlay
          model={model}
          onClose={handleClose}
          onSendEvent={onSendEvent}
          onSendMessage={onSendMessage}
          onRegisterActivityCallback={onRegisterActivityCallback}
          onOverlaySubmit={handleOverlaySubmit}
          container={container}
          sas={sas}
          uri={uri}
        />
      )}
      {botConnection && webChatStore && (
        <ReactWebChatComponent
          directLine={botConnection as FullReactWebChatProps['directLine']}
          store={webChatStore as FullReactWebChatProps['store']}
          styleOptions={styleOptions}
          activityMiddleware={editUserActivityMiddleware}
          locale="en-GB"
        />
      )}
    </WebChatContainer>
  )
}

export default Chat
